import { EditorState } from './core/EditorState.js';
import { CommandHistory } from './core/CommandHistory.js';
import { MapCanvas } from './canvas/MapCanvas.js';
import { ViewportCompositor } from './canvas/ViewportCompositor.js';
import { GridRenderer } from './canvas/GridRenderer.js';
import { CursorPreview } from './canvas/CursorPreview.js';
import { OverlayRenderer } from './canvas/OverlayRenderer.js';
import { SelectionOverlay } from './canvas/SelectionOverlay.js';
import { ToolManager } from './tools/ToolManager.js';
import { PencilTool } from './tools/PencilTool.js';
import { RectTool } from './tools/RectTool.js';
import { FillTool } from './tools/FillTool.js';
import { PropertyBrush } from './tools/PropertyBrush.js';
import { SelectTool } from './tools/SelectTool.js';
import { ObjectTool } from './tools/ObjectTool.js';
import { TerrainBrush } from './tools/TerrainBrush.js';
import { ScatterTool } from './tools/ScatterTool.js';
import { LineTool } from './tools/LineTool.js';
import { TileTaggerPanel } from './ui/TileTaggerPanel.js';
import { TilesetPalette } from './palette/TilesetPalette.js';
import { CustomPalettes } from './palette/CustomPalettes.js';
import { LayerModel } from './layers/LayerModel.js';
import { LayerPanel } from './layers/LayerPanel.js';
import { FileManager } from './io/FileManager.js';
import { TilesetLoader } from './io/TilesetLoader.js';
import { TilesetLibrary } from './io/TilesetLibrary.js';
import { TilesetLibraryPanel } from './ui/TilesetLibraryPanel.js';
import { Toolbar } from './ui/Toolbar.js';
import { StatusBar } from './ui/StatusBar.js';
import { KeyboardShortcuts } from './ui/KeyboardShortcuts.js';
import { MenuBar } from './ui/MenuBar.js';
import { NewMapDialog } from './ui/NewMapDialog.js';
import { PropertyEditor } from './ui/PropertyEditor.js';
import { TabBar } from './ui/TabBar.js';
import { WelcomeScreen } from './ui/WelcomeScreen.js';
import { ExportImageDialog } from './ui/ExportImageDialog.js';
import { ResizeMapDialog } from './ui/ResizeMapDialog.js';
import { ContextMenu } from './ui/ContextMenu.js';
import { TilesetSettingsDialog } from './ui/TilesetSettingsDialog.js';
import { TileAnimationEditor } from './ui/TileAnimationEditor.js';
import { TileCollisionEditor } from './ui/TileCollisionEditor.js';
import { GeneratorPanel } from './generators/ui/GeneratorPanel.js';
import { PluginAPI } from './plugins/PluginAPI.js';
import { DrakkarAdapter } from './plugins/DrakkarAdapter.js';
import { TOOLS } from './utils/constants.js';
import { EchoForgeAuth } from './auth/EchoForgeAuth.js';
import { AuthUI } from './ui/AuthUI.js';
import { CloudStorage } from './io/CloudStorage.js';
import { PixelLabPanel } from './integrations/PixelLabPanel.js';
import { EngineExport } from './integrations/EngineExport.js';
import { GitHubSync } from './integrations/GitHubSync.js';
import { SecureStorage } from './auth/SecureStorage.js';
import { AutoSave } from './io/AutoSave.js';
import { ConnectionMonitor } from './io/ConnectionMonitor.js';
import { UndoHistoryPanel } from './ui/UndoHistoryPanel.js';
import { GitHubDialog } from './ui/GitHubDialog.js';
import { HelpSystem } from './ui/HelpSystem.js';
import { Tutorial } from './ui/Tutorial.js';
import { CommandPalette, buildActions } from './ui/CommandPalette.js';
import { ShortcutOverlay } from './ui/ShortcutOverlay.js';

function boot() {
  // Core state
  const state = new EditorState();
  const commandHistory = new CommandHistory();

  // Create blank default map (16px tiles to match Kenney bundled art)
  state.createBlankMap(40, 30, 16, 16);

  // Canvas rendering
  const mapCanvas = new MapCanvas(state);
  const canvasEl = document.getElementById('map-canvas');
  const compositor = new ViewportCompositor(state, mapCanvas, canvasEl);

  // Overlays
  compositor.addOverlay(new GridRenderer());
  compositor.addOverlay(new OverlayRenderer());
  compositor.addOverlay(new SelectionOverlay());
  compositor.addOverlay(new CursorPreview());

  // Auth & cloud
  const auth = new EchoForgeAuth();
  const cloudStorage = new CloudStorage();
  cloudStorage.init(auth);

  // Connection monitoring + offline mode
  const connectionMonitor = new ConnectionMonitor();
  cloudStorage.bindConnectionMonitor(connectionMonitor);
  connectionMonitor.start();

  // Status bar cloud indicator
  const cloudStatusEl = document.getElementById('status-cloud');
  if (cloudStatusEl) {
    const updateCloudStatus = (s) => {
      if (s.syncing) {
        cloudStatusEl.textContent = '\u2B06 Syncing...';
        cloudStatusEl.style.color = 'var(--accent, #3ba8ff)';
      } else if (s.online) {
        cloudStatusEl.textContent = s.pendingCount > 0
          ? `\u2601 Online (${s.pendingCount} pending)`
          : '\u2601 Online';
        cloudStatusEl.style.color = '#2ecc71';
      } else if (s.networkUp) {
        cloudStatusEl.textContent = '\u2601 API unreachable';
        cloudStatusEl.style.color = '#f39c12';
      } else {
        cloudStatusEl.textContent = '\u2601 Offline';
        cloudStatusEl.style.color = '#e74c3c';
      }
    };
    connectionMonitor.onChange(updateCloudStatus);
    // Set initial state
    updateCloudStatus({ online: false, networkUp: navigator.onLine, syncing: false, pendingCount: 0 });
  }

  // IO
  const fileManager = new FileManager(state, mapCanvas);
  const tilesetLoader = new TilesetLoader(state);
  const tilesetLibrary = new TilesetLibrary(cloudStorage, tilesetLoader);

  // Tools
  const toolManager = new ToolManager(state, compositor, mapCanvas, commandHistory);
  toolManager.registerTool(TOOLS.PENCIL, new PencilTool(state, mapCanvas, commandHistory));
  toolManager.registerTool(TOOLS.ERASER, new PencilTool(state, mapCanvas, commandHistory, true));
  toolManager.registerTool(TOOLS.RECT, new RectTool(state, mapCanvas, commandHistory));
  toolManager.registerTool(TOOLS.FILL, new FillTool(state, mapCanvas, commandHistory));
  toolManager.registerTool(TOOLS.PROPERTY, new PropertyBrush(state, mapCanvas));
  toolManager.registerTool(TOOLS.SELECT, new SelectTool(state));
  toolManager.registerTool(TOOLS.OBJECT, new ObjectTool(state));
  toolManager.registerTool('terrain', new TerrainBrush(state, mapCanvas, commandHistory));
  toolManager.registerTool(TOOLS.SCATTER, new ScatterTool(state, mapCanvas, commandHistory));
  toolManager.registerTool(TOOLS.LINE, new LineTool(state, mapCanvas, commandHistory));

  // Layers
  const layerModel = new LayerModel(state);

  // Tile-level editors
  const tilesetSettings = new TilesetSettingsDialog(state, mapCanvas, compositor);
  const tileAnimationEditor = new TileAnimationEditor(state, mapCanvas, compositor);
  const tileCollisionEditor = new TileCollisionEditor(state, mapCanvas, compositor);

  // Tile tagger (for authoring TileMeta)
  const tileTaggerPanel = new TileTaggerPanel(state, document.createElement('div'));

  // Dialogs
  const generatorPanel = new GeneratorPanel(state, mapCanvas, compositor);
  const newMapDialog = new NewMapDialog(state, mapCanvas, compositor);
  const exportDialog = new ExportImageDialog(state, mapCanvas);
  const resizeDialog = new ResizeMapDialog(state, mapCanvas, compositor);

  // Integrations
  // Secure storage for third-party tokens (encrypted with user's session key)
  const secureStorage = new SecureStorage(() => auth.getUser()?.userId || null);

  const pixelLabPanel = new PixelLabPanel(state, mapCanvas, compositor);
  pixelLabPanel.setSecureStorage(secureStorage);
  const engineExport = new EngineExport(state);
  const githubSync = new GitHubSync(state, fileManager);
  githubSync.setSecureStorage(secureStorage);
  const githubDialog = new GitHubDialog(githubSync, state, fileManager, mapCanvas, compositor);

  // Help system & tutorial
  const tutorial = new Tutorial();
  const helpSystem = new HelpSystem(state);
  helpSystem.setTutorial(tutorial);

  // Custom palettes
  const customPalettes = new CustomPalettes(state, mapCanvas);

  // Plugin API
  const pluginAPI = new PluginAPI(state, compositor, toolManager);

  // Gated plugins — invisible to unauthorized users.
  // No UI trace, no menu items, no console logs, nothing.
  auth.onAuthChange(() => {
    const drakkarAccess = auth.checkFeatureAccess('drakkar');
    if (drakkarAccess.allowed) {
      if (!pluginAPI._plugins.has('drakkar')) {
        pluginAPI.registerPlugin('drakkar', new DrakkarAdapter(auth), [
          'objects', 'overlays', 'menu', 'state:read', 'state:write', 'render',
        ]);
      }
    } else {
      pluginAPI.unregisterPlugin('drakkar');
    }

    // Re-encrypt plaintext tokens on sign-in, clear key cache on sign-out
    if (auth.getUser()) {
      secureStorage.reEncrypt(['github_pat', 'pixellab_api_key']);
    } else {
      secureStorage.clearKeyCache();
    }
  });

  // UI — pass tile editors to TilesetPalette
  new TilesetPalette(state, {
    tilesetSettings,
    tileAnimationEditor,
    tileCollisionEditor,
  });
  new LayerPanel(state, layerModel, mapCanvas);
  new Toolbar(state);
  new StatusBar(state);
  new PropertyEditor(state);
  // Undo history panel (visual timeline)
  const undoHistoryPanel = new UndoHistoryPanel(commandHistory, compositor);

  new KeyboardShortcuts(state, commandHistory, fileManager, compositor, toolManager);
  new ContextMenu(state, compositor, commandHistory, toolManager);
  const menuBar = new MenuBar(state, fileManager, commandHistory, tilesetLoader, compositor, {
    newMapDialog,
    generatorPanel,
    exportDialog,
    resizeDialog,
    pluginAPI,
    pixelLabPanel,
    engineExport,
    githubDialog,
    helpSystem,
    tutorial,
    tilesetLibrary,
    tileTaggerPanel,
    undoHistoryPanel,
  });
  // Auth UI (status bar indicator + sign in dialog)
  const authUI = new AuthUI(auth, cloudStorage);

  // Command palette (Ctrl+P)
  const cmdActions = buildActions(state, {
    fileManager, compositor, commandHistory, toolManager,
    generatorPanel, newMapDialog, exportDialog, resizeDialog, menuBar,
  });
  const commandPalette = new CommandPalette(state, cmdActions);
  const tabBar = new TabBar(state, mapCanvas, compositor);

  // Mount custom palettes
  const customPalettesContainer = document.getElementById('custom-palettes-container');
  if (customPalettesContainer) {
    customPalettes.mount(customPalettesContainer);
  }

  // Palette panel resize handle
  const resizeHandle = document.getElementById('palette-resize-handle');
  const palettePanel = document.getElementById('palette-panel');
  if (resizeHandle && palettePanel) {
    let dragging = false;
    resizeHandle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      dragging = true;
      resizeHandle.classList.add('dragging');
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    });
    window.addEventListener('mousemove', (e) => {
      if (!dragging) return;
      const editorArea = palettePanel.parentElement;
      const editorRect = editorArea.getBoundingClientRect();
      const newWidth = Math.max(200, Math.min(600, e.clientX - editorRect.left));
      palettePanel.style.width = `${newWidth}px`;
      compositor.requestRender();
    });
    window.addEventListener('mouseup', () => {
      if (!dragging) return;
      dragging = false;
      resizeHandle.classList.remove('dragging');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Persist width preference
      try { localStorage.setItem('tileforge_palette_width', palettePanel.style.width); } catch {}
    });
    // Restore saved width
    try {
      const saved = localStorage.getItem('tileforge_palette_width');
      if (saved) palettePanel.style.width = saved;
    } catch {}
  }

  // Re-render when state changes
  state.onChange('layers', () => { mapCanvas.invalidateAll(); compositor.requestRender(); });
  state.onChange('zoom', () => compositor.requestRender());
  state.onChange('scrollX', () => compositor.requestRender());
  state.onChange('scrollY', () => compositor.requestRender());
  state.onChange('showGrid', () => compositor.requestRender());
  state.onChange('showProperties', () => compositor.requestRender());
  state.onChange('cursorTileX', () => compositor.requestRender());
  state.onChange('selectedTileId', () => compositor.requestRender());
  state.onChange('stamp', () => compositor.requestRender());
  state.onChange('selectionStart', () => compositor.requestRender());
  state.onChange('selectionEnd', () => compositor.requestRender());
  state.onChange('tilesetImages', () => { mapCanvas.invalidateAll(); compositor.requestRender(); });
  state.onChange('tileAnimations', () => { mapCanvas.invalidateAll(); compositor.requestRender(); });
  state.onChange('tileCollisions', () => { compositor.requestRender(); });

  // Start animation loop for animated tiles
  mapCanvas.startAnimationLoop(compositor);

  // Handle window resize
  window.addEventListener('resize', () => compositor.requestRender());

  // Drag-and-drop tileset/map loading
  const canvasEl2 = document.getElementById('map-canvas');
  canvasEl2.addEventListener('dragover', (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; });
  canvasEl2.addEventListener('drop', async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;

    const name = file.name.toLowerCase();
    if (name.endsWith('.tmj') || name.endsWith('.json')) {
      // Map file
      try {
        const { validateTMJ } = await import('./io/TMJValidator.js');
        const text = await file.text();
        const result = validateTMJ(text, file.name);
        if (!result.ok) { alert(result.error); return; }
        await fileManager._loadTMJ(result.tmj, file.name);
        compositor.requestRender();
      } catch (err) {
        alert('Failed to load map: ' + err.message);
      }
    } else if (name.endsWith('.psd')) {
      const { PSDImportDialog } = await import('./ui/PSDImportDialog.js');
      const psdDialog = new PSDImportDialog(state, tilesetLoader, compositor);
      psdDialog.show(file);
    } else if (name.endsWith('.png') || name.endsWith('.webp') || name.endsWith('.jpg') || name.endsWith('.jpeg')) {
      // Tileset image — route through tile size picker
      const size = await tilesetLoader._promptTileSize(
        await new Promise((res, rej) => { const img = new Image(); img.onload = () => res(img); img.onerror = rej; img.src = URL.createObjectURL(file); }),
        file.name,
      );
      if (!size) return;
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        tilesetLoader._createTilesetFromImage(img, file.name, url, size.tileW, size.tileH);
        compositor.requestRender();
      };
      img.src = url;
    }
  });

  // Warn before closing with unsaved changes
  window.addEventListener('beforeunload', (e) => {
    if (state.get('dirty')) {
      e.preventDefault();
      e.returnValue = '';
    }
  });

  // Network recovery — refresh auth when coming back online
  window.addEventListener('online', () => {
    if (auth.isAuthenticated()) {
      auth.getTokenAsync(); // triggers refresh if needed
    }
  });

  // Ctrl+N shortcut
  document.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
      e.preventDefault();
      newMapDialog.show();
    }
  });

  // Help button
  const helpBtn = document.getElementById('help-btn');
  if (helpBtn) {
    helpBtn.addEventListener('click', () => window.open('help/index.html', '_blank'));
  }

  // Initial render
  compositor.requestRender();

  // Tileset Library — persistent tileset management
  const tilesetLibraryPanel = new TilesetLibraryPanel(tilesetLibrary, tilesetLoader, state);
  const libContainer = document.getElementById('tileset-library-container');
  if (libContainer) tilesetLibraryPanel.mount(libContainer);

  // Initialize library: provision starter kit on first visit, then auto-load first tileset
  const libraryReady = tilesetLibrary.init().then(async () => {
    await tilesetLibrary.provisionStarterKit();

    // Auto-load first tileset if none loaded
    if (state.get('tilesets').length === 0) {
      const firstTileset = tilesetLibrary.list()[0];
      if (firstTileset) {
        await tilesetLibrary.loadIntoEditor(firstTileset.id);
        mapCanvas.invalidateAll();
        compositor.requestRender();
      }
    }

    tilesetLibraryPanel.render();
  }).catch(err => {
    console.warn('TilesetLibrary init failed, falling back to direct load:', err);
    // Fallback: load bundled tileset directly
    if (state.get('tilesets').length === 0) {
      tilesetLoader.addFromURL('tilesets/bundled/kenney_tiny_town.png', 'Tiny Town (Kenney)')
        .then(() => { mapCanvas.invalidateAll(); compositor.requestRender(); })
        .catch(() => {});
    }
  });

  // Auto-save for crash recovery (30s interval, IndexedDB-backed)
  // Recovery must wait until auth is settled — otherwise the recovery dialog
  // appears over the login/2FA screen, and tileset resolution fails because
  // cloud storage isn't authenticated yet.
  const autoSave = new AutoSave(state);

  function attemptRecovery() {
    // Wait for tileset library before loading recovered maps (otherwise images are missing)
    libraryReady.catch(() => {}).then(() => autoSave.hasRecovery()).then(async (hasRecovery) => {
      if (hasRecovery) {
        const info = await autoSave.getRecoveryInfo();
        if (info && info.age < 7 * 24 * 60 * 60 * 1000) { // ignore snapshots older than 7 days
          const ago = _formatAge(info.age);
          const msg = `TileForge found an unsaved map from ${ago} ago`
            + ` (${info.mapWidth}x${info.mapHeight}, ${info.layerCount} layers).`
            + `\n\nWould you like to recover it?`;
          if (confirm(msg)) {
            try {
              const tmj = await autoSave.getRecoveryData();
              if (tmj) await fileManager._loadTMJ(tmj, info.fileName || 'Recovered');
            } catch (err) {
              console.error('[AutoSave] Recovery failed:', err);
              alert('Recovery failed: ' + err.message);
            }
          }
          await autoSave.clearRecovery();
        }
      }
    }).catch(err => console.warn('[AutoSave] Recovery check failed:', err));
  }

  // Wait for auth to settle before showing recovery dialog.
  // Logto init is async — wait for it to complete before showing recovery.
  auth.waitForSso().then(() => {
    // Small delay so auth UI can settle first
    setTimeout(attemptRecovery, 500);
  }).catch(() => {
    attemptRecovery();
  });

  autoSave.start();

  // Clear auto-save snapshot after successful manual save
  fileManager.onSave(() => autoSave.clearRecovery());

  // Wait for SSO exchange (if portal launched with #efcode=) before showing welcome
  auth.waitForSso().then(() => {
    const welcome = new WelcomeScreen(state, fileManager, tilesetLoader, newMapDialog, generatorPanel, mapCanvas, compositor, auth, authUI);
    welcome.show();

    // Auto-start tutorial after welcome screen closes (first visit only)
    if (!Tutorial.isCompleted()) {
      const welcomeCheck = setInterval(() => {
        if (!document.querySelector('.welcome-overlay')) {
          clearInterval(welcomeCheck);
          setTimeout(() => tutorial.start(), 400);
        }
      }, 200);
    }
  });

  // Expose for debugging
  window.__tileforge = {
    state, commandHistory, mapCanvas, compositor,
    fileManager, tilesetLoader, tilesetLibrary, toolManager,
    generatorPanel, customPalettes, tabBar,
    auth, cloudStorage, githubSync, autoSave, connectionMonitor, undoHistoryPanel,
    tilesetSettings, tileAnimationEditor, tileCollisionEditor,
    commandPalette, shortcutOverlay: new ShortcutOverlay(),
    helpSystem,
    tutorial,
  };

  console.log('TileForge Studio v1.5 loaded');
}

function _formatAge(ms) {
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec} seconds`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min !== 1 ? 's' : ''}`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''}`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days !== 1 ? 's' : ''}`;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
